import { useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { createContext, PropsWithChildren, useEffect } from 'react';
import { matchPath } from 'react-router';
import { useEventBus } from '../use-event-bus';
import { useHistory } from '../use-history-intercept';
import { useLocations } from '../use-locations';
import { ChangeLocationStatus } from '../use-locations/types/change-location-status';
import { useWrapperNavigation } from '../use-wrapper-navigation';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface WrapperBusContextProps {}

const blacklistUrls = ['/locations/:locationId/phone/:phoneNumber'];

export const WrapperBusContext = createContext<WrapperBusContextProps>(null!);

const WrapperBusProvider = ({ children }: PropsWithChildren<object>) => {
  const history = useHistory();
  const { listen } = useEventBus();
  const { signinRedirect, getClaims } = useOidcWithAdmin();
  const { activeFrame, getFrameName } = useWrapperNavigation();
  const { selectedLocationWithStatus } = useLocations();

  useEffect(() => {
    return listen('unauthorized', () => {
      getClaims().then((res) => {
        if (res.userClass != 'GlobalUser') signinRedirect();
      });
    });
  }, [listen, signinRedirect, getClaims]);

  useEffect(() => {
    return listen('navigate', (event) => {
      if (selectedLocationWithStatus?.status !== 'Complete') return;
      if (event.messageType !== 'navigate') return;

      const eventData = event.data;
      const frame = getFrameName(eventData.pathname);

      if (selectedLocationWithStatus?.status === ('InProgress' as ChangeLocationStatus) && frame !== 'core') return;
      // prevent non-active pages from interferring with history
      // frame is null for the wrapper routes
      if (!eventData.crossFrame && (frame !== activeFrame || frame == null)) return;

      if (blacklistUrls.find((url) => !!matchPath(eventData.pathname, url))) return;

      const historyEntry = {
        pathname: eventData.pathname.replace('-frame', ''),
        search: eventData.search || '',
        hash: eventData.hash
      };

      eventData.replaceState ? history.replace(historyEntry) : history.push(historyEntry);
    });
  }, [listen, activeFrame, history, selectedLocationWithStatus?.status]);

  useEffect(() => {
    return listen('absolute-navigate', (event) => {
      if (event.messageType !== 'absolute-navigate') return;
      window.location.href = event.data.url;
    });
  }, [listen]);

  return <WrapperBusContext.Provider value={{}}>{children}</WrapperBusContext.Provider>;
};

export default WrapperBusProvider;
